import type { SxProps, TextFieldProps, Theme } from "@mui/material";
import TextField from "@mui/material/TextField";
import {
  type Control,
  Controller,
  type FieldPath,
  type FieldValues,
  type RegisterOptions,
} from "react-hook-form";
import { useTranslation } from "react-i18next";

export interface ReactHookNumberInputProps<T extends FieldValues> {
  name: FieldPath<T>;
  label: string;
  rules?: Omit<
    RegisterOptions<T, FieldPath<T>>,
    "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
  >;
  required?: boolean;
  sx?: SxProps<Theme>;
  control?: Control<T>;
  fullWidth?: boolean;
  textFieldProps?: TextFieldProps;
}
export function ReactHookNumberInput<T extends FieldValues>({
  name,
  label,
  rules,
  required = false,
  sx,
  control,
  fullWidth = false,
  textFieldProps,
}: ReactHookNumberInputProps<T>) {
  const { t } = useTranslation("Fixhub");
  const labelPrefix = required ? "* " : "";
  const actualLabel = `${labelPrefix}${label}`;

  return (
    <Controller
      name={name}
      rules={{
        required: required
          ? t("Next:Core:FieldRequiredValidationMessage")
          : false,
        validate: {
          isNumber: (value) =>
            Number.isFinite(value) ||
            t("Next:Core:NumberInputValidationMessage"),
        },
        ...rules,
      }}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <TextField
          {...field}
          value={
            Number.isNaN(field.value) || field.value === 0
              ? ""
              : field.value.toString()
          }
          onChange={(e) => {
            const output = parseInt(e.target.value, 10);
            field.onChange(Number.isNaN(output) ? 0 : output);
          }}
          fullWidth={fullWidth}
          inputRef={field.ref}
          label={actualLabel}
          error={Boolean(error)}
          helperText={error?.message}
          // https://mui.com/components/text-fields/#type-quot-number-quot
          onFocus={(e) => {
            field.onChange(e.target.value === "0" ? "" : e.target.value);
          }}
          onBlur={(e) => {
            field.onChange(parseFloat(e.target.value) || 0);
          }}
          sx={sx}
          {...textFieldProps}
        />
      )}
    />
  );
}
